import { createIcon, type IconName } from "./icons";

export interface ContextMenuItem {
  label: string;
  onSelect: () => void;
  /** Grisée et non cliquable (ex. "Descendre" quand `Shape.layer` est déjà au minimum). Défaut : false. */
  disabled?: boolean;
  /**
   * Raccourci clavier associé (ex. `"Ctrl+C"`), affiché à droite du label — purement informatif,
   * n'enregistre aucun binding ici (voir `packages/example/src/useKeyboardShortcuts.ts`, seul
   * endroit où ces raccourcis sont réellement câblés). Omis : rien affiché à droite du label.
   */
  shortcut?: string;
  /** Icône monochrome (voir `ui/icons.ts`) affichée à gauche du label. Omise : pas d'icône. */
  icon?: IconName;
}

/** Menu contextuel (BESOINS.md §4) : ouvert par clic droit sur une forme. */
export class ContextMenu {
  private element: HTMLDivElement | null = null;

  constructor(private container: HTMLElement) {}

  show(position: { x: number; y: number }, items: ContextMenuItem[]): void {
    this.close();

    const menu = document.createElement("div");
    menu.className = "context-menu";

    for (const item of items) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "context-menu__item";
      button.disabled = item.disabled ?? false;

      // Regroupe icône + label dans un seul flex item (`main`) : `.context-menu__item` répartit
      // ses enfants directs via `justify-content: space-between` (voir style.css) pour écarter le
      // label du raccourci — sans ce wrapper, l'icône (flex item à part entière) se retrouverait
      // elle-même écartée du label au lieu de rester collée à côté.
      const main = document.createElement("span");
      main.className = "context-menu__main";

      if (item.icon) {
        const iconWrapper = document.createElement("span");
        iconWrapper.className = "context-menu__icon";
        iconWrapper.appendChild(createIcon(item.icon));
        main.appendChild(iconWrapper);
      }

      const label = document.createElement("span");
      label.className = "context-menu__label";
      label.textContent = item.label;
      main.appendChild(label);
      button.appendChild(main);

      if (item.shortcut) {
        // Espace explicite (nœud texte séparé, pas fusionné dans l'un ou l'autre `textContent`) :
        // le layout flex (`.context-menu__item`, style.css) les écarte visuellement de toute façon,
        // mais un `textContent`/`allTextContents()` (voir e2e) sans ce nœud les collerait ("CopierCtrl+C").
        button.appendChild(document.createTextNode(" "));
        const shortcut = document.createElement("span");
        shortcut.className = "context-menu__shortcut";
        shortcut.textContent = item.shortcut;
        button.appendChild(shortcut);
      }

      button.addEventListener("click", () => {
        item.onSelect();
        this.close();
      });
      menu.appendChild(button);
    }

    this.container.appendChild(menu);
    this.element = menu;

    // Repositionné après insertion dans le DOM (seul moment où `offsetWidth`/`offsetHeight` sont
    // fiables) pour ne jamais déborder de la fenêtre — `.context-menu` est en `position: fixed`
    // (style.css), donc ces bornes sont directement celles du viewport.
    const maxX = window.innerWidth - menu.offsetWidth;
    const maxY = window.innerHeight - menu.offsetHeight;
    menu.style.left = `${Math.max(0, Math.min(position.x, maxX))}px`;
    menu.style.top = `${Math.max(0, Math.min(position.y, maxY))}px`;

    // Se ferme au premier appui hors du menu. Écouté dès l'ouverture : le geste qui ouvre le menu a
    // déjà émis son `pointerdown`, et attendre un tick laissait passer un clic sur une page chargée.
    document.addEventListener("pointerdown", this.handleOutsidePointerDown, true);
  }

  close(): void {
    document.removeEventListener("pointerdown", this.handleOutsidePointerDown, true);
    this.element?.remove();
    this.element = null;
  }

  private handleOutsidePointerDown = (event: PointerEvent): void => {
    if (this.element?.contains(event.target as Node)) return;
    this.close();
  };
}
